import { useEffect, useState } from 'react'
import { deviceId } from '../lib/geo'
import { db, localRefOptions } from '../lib/db'
import { queue } from '../lib/sync'
import { queueAssessment } from '../lib/outbox'
import { t } from '../lib/i18n'
import type { Household } from './HouseholdScreen'

/**
 * RECORDING WHAT HAS CHANGED — a new assessment, not an edit.
 *
 * Everything on this screen is an indicator: it has a value, a date, and a
 * person who observed it. Saving writes NEW observations. The old ones stay
 * exactly where they were, which is the only reason the platform can answer
 * "what was latrine coverage in this village last December" — a question no
 * amount of later work can reconstruct once the old value is overwritten.
 *
 * The screen opens pre-filled with today's known values, and sends only the
 * ones the officer actually touched. Sending an untouched value would create a
 * fresh observation asserting "I saw this today" for something nobody looked
 * at, which is a quiet lie that shows up later as suspiciously perfect data.
 */

type RefOption = { list_code: string; code: string; label_en: string; label_km: string | null }

const COUNTS = [
  ['adults_male_18_59', 'Adult men 18–59', 'បុរស ១៨-៥៩'],
  ['adults_female_18_59', 'Adult women 18–59', 'ស្ត្រី ១៨-៥៩'],
  ['children_under_18', 'Children under 18', 'កុមារក្រោម ១៨'],
  ['elderly_60_plus', 'People 60 and over', 'អាយុ ៦០ ឡើង'],
  ['members_with_disability', 'People with a disability', 'ជនពិការ'],
] as const

type CountKey = (typeof COUNTS)[number][0]

export function UpdateAssessmentScreen({
  household,
  onDone,
  onCancel,
}: {
  household: Household
  onDone: () => void
  onCancel: () => void
}) {
  const [options, setOptions] = useState<RefOption[]>([])

  const original: Record<CountKey, number> = {
    adults_male_18_59: household.adults_male_18_59,
    adults_female_18_59: household.adults_female_18_59,
    children_under_18: household.children_under_18,
    elderly_60_plus: household.elderly_60_plus,
    members_with_disability: household.members_with_disability,
  }

  const [counts, setCounts] = useState<Record<CountKey, number>>(original)
  const [hasLatrine, setHasLatrine] = useState<boolean | null>(household.has_latrine)
  const [latrineType, setLatrineType] = useState(household.latrine_type ?? '')
  const [waterSource, setWaterSource] = useState(household.water_source ?? '')
  const [notes, setNotes] = useState('')

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    localRefOptions(['latrine_type', 'water_source']).then((rows) =>
      setOptions(rows as RefOption[]),
    )
  }, [])

  const opts = (list: string) => options.filter((o) => o.list_code === list)

  function bump(key: CountKey, by: number) {
    setCounts((c) => ({ ...c, [key]: Math.max(0, Math.min(60, c[key] + by)) }))
  }

  const size =
    counts.adults_male_18_59 +
    counts.adults_female_18_59 +
    counts.children_under_18 +
    counts.elderly_60_plus

  /** Only what the officer actually changed. See the note at the top. */
  function changedValues(): Record<string, unknown> {
    const v: Record<string, unknown> = {}
    for (const [key] of COUNTS) if (counts[key] !== original[key]) v[key] = counts[key]
    if (hasLatrine !== household.has_latrine && hasLatrine !== null) v.has_latrine = hasLatrine
    if (latrineType && latrineType !== (household.latrine_type ?? '')) v.latrine_type = latrineType
    if (waterSource && waterSource !== (household.water_source ?? '')) v.water_source = waterSource
    return v
  }

  const values = changedValues()
  const nothingToSave = Object.keys(values).length === 0

  async function save() {
    setError(null)
    setSaving(true)
    try {
      const observedAt = new Date().toISOString()

      // The phone's snapshot, so the list and this screen agree at once.
      await db.households.update(household.household_uid, {
        ...(values.adults_male_18_59 !== undefined ? { adults_male_18_59: counts.adults_male_18_59 } : {}),
        ...(values.adults_female_18_59 !== undefined ? { adults_female_18_59: counts.adults_female_18_59 } : {}),
        ...(values.children_under_18 !== undefined ? { children_under_18: counts.children_under_18 } : {}),
        ...(values.elderly_60_plus !== undefined ? { elderly_60_plus: counts.elderly_60_plus } : {}),
        ...(values.members_with_disability !== undefined ? { members_with_disability: counts.members_with_disability } : {}),
        ...(values.has_latrine !== undefined ? { has_latrine: hasLatrine } : {}),
        ...(values.latrine_type !== undefined ? { latrine_type: latrineType } : {}),
        ...(values.water_source !== undefined ? { water_source: waterSource } : {}),
        household_size: size,
        last_assessed_at: observedAt,
        updated_at: observedAt,
        pending: true,
      })

      await queue(
        queueAssessment({
          household_uid: household.household_uid,
          values,
          purpose: 'reassessment',
          // The moment the officer stood there — not the moment it uploads.
          observed_at: observedAt,
          notes: notes.trim() || null,
          device: deviceId(),
        }),
      )
      onDone()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  const selectStyle = {
    width: '100%',
    minHeight: 48,
    fontSize: 16,
    borderRadius: 10,
    border: '2px solid var(--line)',
    padding: '10px 12px',
  }

  return (
    <div className="screen">
      <div className="appbar">
        <h1>{t('up.title')} · {household.household_code ?? ''}</h1>
        <button className="btn-quiet" onClick={onCancel}>
          {t('common.cancel')}
        </button>
      </div>

      <div className="content">
        <div className="alert" style={{ borderColor: 'var(--line)' }}>
          {t('up.onlyDifferent')}
        </div>

        <div className="card" style={{ maxWidth: 'none', marginTop: 14 }}>
          <h3 style={{ marginTop: 0 }}>{t('up.people')}</h3>
          {COUNTS.map(([key, en, km]) => (
            <div
              key={key}
              style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{en}</div>
                <div className="muted km">{km}</div>
              </div>
              <button style={{ width: 48 }} onClick={() => bump(key, -1)} aria-label={`fewer ${en}`}>
                −
              </button>
              <span
                style={{
                  minWidth: 32,
                  textAlign: 'center',
                  fontSize: 20,
                  fontWeight: 700,
                  color: counts[key] !== original[key] ? 'var(--green-900)' : undefined,
                }}
              >
                {counts[key]}
              </span>
              <button style={{ width: 48 }} onClick={() => bump(key, +1)} aria-label={`more ${en}`}>
                +
              </button>
            </div>
          ))}
          <p className="muted" style={{ marginBottom: 0 }}>
            {t('add.householdSize')}: <strong>{size}</strong>
            {size !== household.household_size && <> ({t('up.was')} {household.household_size})</>}
          </p>
        </div>

        <div className="card" style={{ maxWidth: 'none', marginTop: 14 }}>
          <h3 style={{ marginTop: 0 }}>{t('up.sanitation')}</h3>

          <label>{t('add.hasLatrine')}</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {[
              [true, t('common.yes')],
              [false, t('common.no')],
            ].map(([v, l]) => (
              <button
                key={String(l)}
                onClick={() => setHasLatrine(v as boolean)}
                className={hasLatrine === v ? 'btn-primary' : ''}
                style={
                  hasLatrine === v
                    ? undefined
                    : { border: '2px solid var(--line)', background: 'var(--white)' }
                }
              >
                {l as string}
              </button>
            ))}
          </div>

          {hasLatrine === true && (
            <>
              <label htmlFor="lt">{t('add.latrineType')}</label>
              <select
                id="lt"
                value={latrineType}
                onChange={(e) => setLatrineType(e.target.value)}
                style={selectStyle}
              >
                <option value="">{t('common.notRecorded')}</option>
                {opts('latrine_type').map((o) => (
                  <option key={o.code} value={o.code}>
                    {o.label_en}
                  </option>
                ))}
              </select>
            </>
          )}

          <label htmlFor="ws">{t('add.waterSource')}</label>
          <select
            id="ws"
            value={waterSource}
            onChange={(e) => setWaterSource(e.target.value)}
            style={selectStyle}
          >
            <option value="">{t('common.notRecorded')}</option>
            {opts('water_source').map((o) => (
              <option key={o.code} value={o.code}>
                {o.label_en}
              </option>
            ))}
          </select>

          <label htmlFor="notes">{t('up.visitNote')}</label>
          <input
            id="notes"
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. latrine built with project support in July"
          />
        </div>

        {error && (
          <div className="alert alert-error" style={{ marginTop: 14 }}>
            {error}
          </div>
        )}

        <div style={{ margin: '18px 0 40px' }}>
          <button className="btn-primary" onClick={save} disabled={saving || nothingToSave}>
            {saving
              ? 'Saving…'
              : `Save ${Object.keys(values).length || 'no'} change${
                  Object.keys(values).length === 1 ? '' : 's'
                }`}
          </button>
          {nothingToSave && (
            <p className="muted">
              {t('up.nothingYet')}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
